'use client';

import * as DialogPrimitive from '@radix-ui/react-dialog';
import X from 'lucide-react/dist/esm/icons/x';
import * as React from 'react';

import { cn } from '@/lib/utils';

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = ({
  className,
  container,
  ref,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay> & {
  container?: HTMLElement | null;
}) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn('inset-0 z-50 bg-black/80', container ? 'absolute' : 'fixed', className)}
    {...props}
  />
);
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = ({
  className,
  children,
  closeable = true,
  container,
  overlayClassName,
  ref,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  closeable?: boolean;
  container?: HTMLElement | null;
  overlayClassName?: string;
}) => {
  return (
    <DialogPortal container={container}>
      <DialogOverlay container={container} className={overlayClassName} />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          'top-[50%] left-[50%] z-50 grid min-h-0 w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 overflow-y-auto border border-border bg-background p-6 shadow-lg focus-visible:outline-none sm:rounded-lg',
          container ? 'absolute max-h-[90%]' : 'fixed max-h-[90vh]',
          className
        )}
        data-radix-dialog-content=""
        {...props}
      >
        {children}
        {closeable ? (
          <DialogPrimitive.Close className="absolute top-4 right-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-none disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
            <X className="size-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        ) : null}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
};
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col gap-1.5 text-center sm:text-left', className)} {...props} />
);
DialogHeader.displayName = 'DialogHeader';

type DialogFooterProps = React.HTMLAttributes<HTMLDivElement> & {
  /**
   * The escape action (Cancel, Close). It sits alone at the leading edge and the children
   * group at the trailing edge, last child rightmost. Leave it unset for a footer whose only
   * button dismisses the dialog, so that button stays at the trailing edge.
   */
  start?: React.ReactNode;
};

const DialogFooter = ({ className, start, children, ...props }: DialogFooterProps) =>
  start ? (
    <div
      className={cn(
        'flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between',
        className
      )}
      {...props}
    >
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center">{start}</div>
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center">{children}</div>
    </div>
  ) : (
    <div
      className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}
      {...props}
    >
      {children}
    </div>
  );
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = ({
  className,
  ref,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-lg leading-none font-semibold tracking-tight', className)}
    {...props}
  />
);
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = ({
  className,
  ref,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
);
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger
};
